import { CalendarDay, WEEKDAY_NAMES } from '../../utils/calendar-utils'
import { AgendaEvent } from '@/lib/api/common/interfaces/agenda.interface'
import { EventCard } from '../events/EventCard'
import { useState } from 'react'

interface MonthViewProps {
    days: CalendarDay[]
    events: AgendaEvent[]
    onDateClick?: (date: Date) => void
    onEventClick?: (event: AgendaEvent) => void
}

export function MonthView({
    days,
    events,
    onDateClick,
    onEventClick,
}: MonthViewProps) {
    const [expandedDay, setExpandedDay] = useState<string | null>(null)

    const getEventsForDay = (date: Date): AgendaEvent[] => {
        return events.filter(event => {
            const eventDate = new Date(event.startDate)
            return (
                eventDate.getDate() === date.getDate() &&
                eventDate.getMonth() === date.getMonth() &&
                eventDate.getFullYear() === date.getFullYear()
            )
        })
    }

    const handleDayClick = (day: CalendarDay) => {
        if (day.isPast) return
        if (onDateClick) {
            onDateClick(day.date)
        }
    }

    const handleShowMore = (dateKey: string, e: React.MouseEvent) => {
        e.stopPropagation()
        setExpandedDay(expandedDay === dateKey ? null : dateKey)
    }

    const weeks: CalendarDay[][] = []
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7))
    }

    return (
        <div className="flex flex-col h-full">
            <div className="grid grid-cols-7 border-b border-base-300">
                {WEEKDAY_NAMES.map(day => (
                    <div
                        key={day}
                        className="px-2 py-3 text-center text-sm font-medium text-base-content/70 border-r border-base-300 last:border-r-0"
                    >
                        {day}
                    </div>
                ))}
            </div>

            <div className="flex-1 grid grid-rows-6">
                {weeks.map((week, weekIndex) => (
                    <div key={weekIndex} className="grid grid-cols-7 border-b border-base-300 last:border-b-0">
                        {week.map((day, dayIndex) => {
                            const dayEvents = getEventsForDay(day.date)
                            const dateKey = day.date.toISOString()
                            const isExpanded = expandedDay === dateKey
                            const maxVisible = 2
                            const hasMore = dayEvents.length > maxVisible

                            return (
                                <div
                                    key={dayIndex}
                                    className={`min-h-[100px] border-r border-base-300 last:border-r-0 p-1 transition-colors ${
                                        day.isPast
                                            ? 'bg-base-200/50 cursor-not-allowed'
                                            : 'hover:bg-base-200/30 cursor-pointer'
                                    } ${
                                        !day.isCurrentMonth
                                            ? 'opacity-40'
                                            : ''
                                    }`}
                                    onClick={() => handleDayClick(day)}
                                >
                                    <div
                                        className={`text-sm font-medium mb-1 w-7 h-7 flex items-center justify-center rounded-full ${
                                            day.isToday
                                                ? 'bg-secondary text-secondary-content'
                                                : 'text-base-content'
                                        }`}
                                    >
                                        {day.dayNumber}
                                    </div>

                                    <div className="flex flex-col gap-1">
                                        {dayEvents
                                            .slice(
                                                0,
                                                isExpanded
                                                    ? dayEvents.length
                                                    : maxVisible
                                            )
                                            .map(event => (
                                                <EventCard
                                                    key={event.id}
                                                    event={event}
                                                    onClick={onEventClick}
                                                    compact
                                                />
                                            ))}
                                        {hasMore && !isExpanded && (
                                            <button
                                                className="text-xs text-secondary hover:underline text-left"
                                                onClick={e =>
                                                    handleShowMore(dateKey, e)
                                                }
                                            >
                                                Ver mais ({dayEvents.length - maxVisible})
                                            </button>
                                        )}
                                        {isExpanded && (
                                            <button
                                                className="text-xs text-secondary hover:underline text-left"
                                                onClick={e =>
                                                    handleShowMore(dateKey, e)
                                                }
                                            >
                                                Ver menos
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                ))}
            </div>
        </div>
    )
}
